import { useState, useMemo, useEffect, useCallback } from "react";
import invariant from "tiny-invariant";

export type CountdownState = "idle" | "counting" | "done";

export interface UseCountdownOptions {
  startImmediately?: boolean;
  onDone?: () => void;
}

export function useCountdown(
  countdownLength: number,
  { startImmediately = false, onDone }: UseCountdownOptions,
) {
  const [state, setState] = useState<CountdownState>(startImmediately ? "counting" : "idle");
  const [remaining, setRemaining] = useState(countdownLength);
  const targetDate = useMemo(
    () => (state === "counting" ? new Date(Date.now() + countdownLength) : null),
    [countdownLength, state],
  );

  useEffect(() => {
    let mounted = true;

    function updateRemaining() {
      if (state !== "counting" || !mounted) return;

      invariant(targetDate, "`targetDate` should be defined when `state` is `counting`");
      const newRemaining = Math.max(0, targetDate.getTime() - Date.now());
      setRemaining(newRemaining);

      if (newRemaining === 0) {
        setState("done");
        onDone?.();
      }

      requestAnimationFrame(updateRemaining);
    }

    requestAnimationFrame(updateRemaining);

    return () => {
      mounted = false;
    };
  }, [onDone, state, targetDate]);

  const start = useCallback(() => {
    if (state === "counting") return;
    setState("counting");
    setRemaining(countdownLength);
  }, [countdownLength, state]);

  const stop = useCallback(() => {
    setState("idle");
    setRemaining(countdownLength);
  }, [countdownLength]);

  return [remaining, { start, stop, state }] as const;
}
